import React, { useState } from 'react';
import { getApiBase } from '../../utils/api';
import {
  X, Building2, User, Mail, Phone, MapPin, Users, Briefcase,
  Plane, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';

const EarlyAccessForm = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Company Information
    company_name: '',
    industry: '',
    employee_count: '',
    website: '',
    headquarters_address: '',
    headquarters_city: '',
    headquarters_state: '',
    headquarters_country: '',

    // Requester Information
    requester_name: '',
    requester_email: '',
    requester_phone: '',
    requester_title: '',
    requester_department: '',

    // Executive Travel Details
    num_executive_assistants: '',
    num_executives_supported: '',
    monthly_trips_managed: '',

    // Current Travel Management
    has_travel_agency: '',
    travel_agency_name: '',
    agency_mandatory: '',
    agency_exceptions: '',
    current_tools: [],
    other_tools: '',

    // Pain Points & Needs
    biggest_challenges: [],
    other_challenges: '',
    flight_tracking_method: '',
    itinerary_change_handling: '',
    most_valuable_features: [],
    other_features: '',

    // Additional Information
    how_heard_about_us: '',
    additional_comments: ''
  });

  const industries = [
    'Technology',
    'Finance & Banking',
    'Healthcare',
    'Legal Services',
    'Consulting',
    'Real Estate',
    'Manufacturing',
    'Energy & Utilities',
    'Media & Entertainment',
    'Retail & Consumer Goods',
    'Transportation & Logistics',
    'Government',
    'Non-Profit',
    'Other'
  ];

  const employeeRanges = [
    '1-50',
    '51-200',
    '201-500',
    '501-1000',
    '1001-5000',
    '5000+'
  ];

  const travelTools = [
    'Spreadsheets (Excel/Google Sheets)',
    'Email/Calendar only',
    'Concur',
    'Egencia',
    'TripIt',
    'SAP Concur',
    'Navan (TripActions)',
    'Corporate travel agency portal',
    'Custom internal system',
    'Other'
  ];

  const challengeOptions = [
    'Tracking flight status changes in real-time',
    'Consolidating travel information from multiple sources',
    'Communicating itinerary changes to executives',
    'Managing last-minute changes and rebookings',
    'Coordinating between multiple EAs',
    'Keeping track of executive preferences',
    'Managing ground transportation logistics',
    'Document organization and retrieval',
    'Visibility into upcoming travel across executives',
    'Other'
  ];

  const featureOptions = [
    'Real-time flight tracking and alerts',
    'Automated email parsing for itineraries',
    'Centralized dashboard for all travel',
    'Mobile access for executives',
    'Team collaboration features',
    'Integration with existing travel agency',
    'Preference management for executives',
    'Document storage and organization',
    'Calendar integration',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      const currentArray = formData[name] || [];
      if (checked) {
        setFormData({ ...formData, [name]: [...currentArray, value] });
      } else {
        setFormData({ ...formData, [name]: currentArray.filter(item => item !== value) });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.company_name && formData.industry && formData.employee_count;
      case 2:
        return formData.requester_name && formData.requester_email && formData.requester_title;
      case 3:
        return formData.num_executive_assistants && formData.num_executives_supported;
      case 4:
        return formData.has_travel_agency !== '';
      case 5:
        return formData.biggest_challenges.length > 0;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${getApiBase()}/early-access/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to submit request. Please try again.');
      }
    } catch (err) {
      console.error('Failed to submit early access request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-white rounded-2xl max-w-lg w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Request Submitted!</h2>
          <p className="text-slate-600 mb-6">
            Thank you for your interest in Veyage. We'll review your information and reach out
            within 2-3 business days to discuss next steps.
          </p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900">Request Early Access</h2>
            <p className="text-sm text-slate-500">Step {step} of 5</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 bg-slate-50">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  s <= step ? 'bg-purple-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Step 1: Company Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Company Information</h3>
                  <p className="text-sm text-slate-500">Tell us about your organization</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter company name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Industry <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select industry</option>
                    {industries.map(ind => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Employees <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="employee_count"
                    value={formData.employee_count}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select range</option>
                    {employeeRanges.map(range => (
                      <option key={range} value={range}>{range}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Company Website
                </label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Headquarters Address
                </label>
                <input
                  type="text"
                  name="headquarters_address"
                  value={formData.headquarters_address}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                  <input
                    type="text"
                    name="headquarters_city"
                    value={formData.headquarters_city}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State/Province</label>
                  <input
                    type="text"
                    name="headquarters_state"
                    value={formData.headquarters_state}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                  <input
                    type="text"
                    name="headquarters_country"
                    value={formData.headquarters_country}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Requester Information */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Your Information</h3>
                  <p className="text-sm text-slate-500">Tell us about yourself</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="requester_name"
                    value={formData.requester_name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Job Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="requester_title"
                    value={formData.requester_title}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Executive Assistant, Office Manager"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Work Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="requester_email"
                  value={formData.requester_email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="you@company.com"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="requester_phone"
                    value={formData.requester_phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Department
                  </label>
                  <input
                    type="text"
                    name="requester_department"
                    value={formData.requester_department}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Executive Office, Administration"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Executive Travel Details */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Executive Travel Details</h3>
                  <p className="text-sm text-slate-500">Help us understand your travel management needs</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Executive Assistants <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="num_executive_assistants"
                    value={formData.num_executive_assistants}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="1">1</option>
                    <option value="2-5">2-5</option>
                    <option value="6-10">6-10</option>
                    <option value="11-25">11-25</option>
                    <option value="26-50">26-50</option>
                    <option value="50+">50+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Executives Supported <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="num_executives_supported"
                    value={formData.num_executives_supported}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    <option value="1-5">1-5</option>
                    <option value="6-15">6-15</option>
                    <option value="16-30">16-30</option>
                    <option value="31-50">31-50</option>
                    <option value="51-100">51-100</option>
                    <option value="100+">100+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Approximate Trips Managed Per Month
                </label>
                <select
                  name="monthly_trips_managed"
                  value={formData.monthly_trips_managed}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="1-10">1-10</option>
                  <option value="11-25">11-25</option>
                  <option value="26-50">26-50</option>
                  <option value="51-100">51-100</option>
                  <option value="100+">100+</option>
                </select>
              </div>

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <strong>Note:</strong> This information helps us understand the scale of your travel
                  management needs and ensure our platform is a good fit for your organization.
                </p>
              </div>
            </div>
          )}

          {/* Step 4: Current Travel Management */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Plane className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Current Travel Management</h3>
                  <p className="text-sm text-slate-500">How do you currently manage corporate travel?</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Do you work with a corporate travel agency or TMC? <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {['Yes', 'No', 'Multiple agencies'].map(option => (
                    <label key={option} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <input
                        type="radio"
                        name="has_travel_agency"
                        value={option}
                        checked={formData.has_travel_agency === option}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-slate-700">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              {(formData.has_travel_agency === 'Yes' || formData.has_travel_agency === 'Multiple agencies') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Travel Agency/TMC Name(s)
                    </label>
                    <input
                      type="text"
                      name="travel_agency_name"
                      value={formData.travel_agency_name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="e.g., American Express GBT, CWT, BCD Travel"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Is using the travel agency mandatory for all employees?
                    </label>
                    <div className="space-y-2">
                      {[
                        'Yes, all employees must use the agency',
                        'Yes, but executives have flexibility',
                        'No, it\'s optional but preferred',
                        'No, employees can book independently'
                      ].map(option => (
                        <label key={option} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                          <input
                            type="radio"
                            name="agency_mandatory"
                            value={option}
                            checked={formData.agency_mandatory === option}
                            onChange={handleChange}
                            className="w-4 h-4 text-purple-600"
                          />
                          <span className="text-slate-700">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Are there exceptions? (e.g., C-suite can book directly, international travel only)
                    </label>
                    <textarea
                      name="agency_exceptions"
                      value={formData.agency_exceptions}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows="3"
                      placeholder="Describe any exceptions to your travel policy..."
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What tools do you currently use to manage executive travel? (Select all that apply)
                </label>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {travelTools.map(tool => (
                    <label key={tool} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        name="current_tools"
                        value={tool}
                        checked={formData.current_tools.includes(tool)}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-slate-700">{tool}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.current_tools.includes('Other') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Please specify other tools
                  </label>
                  <input
                    type="text"
                    name="other_tools"
                    value={formData.other_tools}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 5: Pain Points & Additional Info */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Your Needs & Challenges</h3>
                  <p className="text-sm text-slate-500">Help us understand how we can best serve you</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What are your biggest challenges with executive travel management? <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                  {challengeOptions.map(challenge => (
                    <label key={challenge} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        name="biggest_challenges"
                        value={challenge}
                        checked={formData.biggest_challenges.includes(challenge)}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-slate-700">{challenge}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.biggest_challenges.includes('Other') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Please describe other challenges
                  </label>
                  <input
                    type="text"
                    name="other_challenges"
                    value={formData.other_challenges}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  How do you currently track flight status changes?
                </label>
                <textarea
                  name="flight_tracking_method"
                  value={formData.flight_tracking_method}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="e.g., Manually check airline apps, FlightAware, rely on the travel agency..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  How do you communicate itinerary changes to executives?
                </label>
                <textarea
                  name="itinerary_change_handling"
                  value={formData.itinerary_change_handling}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="2"
                  placeholder="e.g., Email updates, text messages, calendar updates..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  What features would be most valuable to you? (Select all that apply)
                </label>
                <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto">
                  {featureOptions.map(feature => (
                    <label key={feature} className="flex items-center gap-3 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        name="most_valuable_features"
                        value={feature}
                        checked={formData.most_valuable_features.includes(feature)}
                        onChange={handleChange}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  How did you hear about us?
                </label>
                <select
                  name="how_heard_about_us"
                  value={formData.how_heard_about_us}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="Referral from colleague">Referral from colleague</option>
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="Google search">Google search</option>
                  <option value="Industry event">Industry event</option>
                  <option value="Urbanride">Urbanride</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Anything else you'd like us to know?
                </label>
                <textarea
                  name="additional_comments"
                  value={formData.additional_comments}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows="3"
                  placeholder="Any additional information that would help us understand your needs..."
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex justify-between">
          {step > 1 ? (
            <button
              onClick={prevStep}
              className="px-6 py-2 text-slate-700 hover:bg-slate-100 rounded-lg font-medium transition-colors"
            >
              Back
            </button>
          ) : (
            <div />
          )}

          {step < 5 ? (
            <button
              onClick={nextStep}
              disabled={!validateStep(step)}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading || !validateStep(step)}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EarlyAccessForm;
